import * as React from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link as MuiLink,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
    approvePrivateClubJoinRequest,
    deleteUserPrivateClub,
    getPrivateClubCurrentRoundMatches,
    getPrivateClubJoinRequests,
    getPrivateClubTemplates,
    getUserPrivateClub,
    getUserPrivateClubLeaderboard,
    rejectPrivateClubJoinRequest,
    removePrivateClubMember,
    updateUserPrivateClub,
} from '../Api';
import PrivateClubDto, {
    PrivateClubJoinRequestDto,
    PrivateClubLeaderboardEntryDto,
    PrivateClubRoundStatus,
    PrivateClubTemplateDto,
    PrivateClubUserInfoDto,
} from '../models/dtos/PrivateClubDto';
import {
    PrivateClubCurrentRoundAdminDto,
    PrivateClubCurrentRoundMatchAdminDto,
} from '../models/dtos/PrivateClubAdminDto';
import PrivateClubTestAsUserDialog from '../components/PrivateClubTestAsUserDialog';
import PrivateClubMatchSummary from '../components/PrivateClubMatchSummary';
import AddPrivateClubMemberDialog from '../components/AddPrivateClubMemberDialog';

const betGroupFallbackNames: Record<number, string> = {
    1: '1x2',
    5: 'Total over',
    6: 'Total under',
    7: 'Both teams to score',
    46: 'Player to score',
    47: 'Player not to score',
};

function formatDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '-';
}

function formatBetcoins(value: number) {
    return value.toLocaleString();
}

function statusColor(status: string): 'success' | 'warning' | 'default' {
    if (status === 'ACTIVE' || status === 'OPEN') return 'success';
    if (status === 'SCHEDULED') return 'warning';
    return 'default';
}

function roundStatusColor(status: PrivateClubRoundStatus): 'success' | 'warning' | 'default' {
    if (status === 'OPEN') return 'success';
    if (status === 'SCHEDULED' || status === 'PROCESSING') return 'warning';
    return 'default';
}

function userDisplayName(user: PrivateClubUserInfoDto | null | undefined, userId: number) {
    return user?.name?.trim() || `User ${userId}`;
}

function ownerName(club: PrivateClubDto) {
    return club.ownerUser?.name?.trim() || `User ${club.createdBy}`;
}

function userCountry(user?: PrivateClubUserInfoDto | null) {
    return user?.country ?? null;
}

function userLanguage(user?: PrivateClubUserInfoDto | null) {
    return user?.language?.trim()?.toUpperCase() || '—';
}

function UserIdentityCell({ userId, user }: { userId: number; user?: PrivateClubUserInfoDto | null }) {
    return (
        <Stack direction="row" gap={1} alignItems="center">
            <Avatar src={user?.photoUrl} sx={{ width: 28, height: 28 }} />
            <MuiLink
                component={RouterLink}
                to={`/users/${userId}`}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
            >
                {userDisplayName(user, userId)}
            </MuiLink>
        </Stack>
    );
}

function UserLocaleCell({ user }: { user?: PrivateClubUserInfoDto | null }) {
    const country = userCountry(user);
    return (
        <Stack alignItems="center" justifyContent="center" spacing={0.5} sx={{ width: 72 }}>
            {country ? (
                <Tooltip title={country.name || user?.countryCode || ''} arrow>
                    <img
                        src={country.icon}
                        width={24}
                        height={24}
                        style={{ cursor: 'pointer' }}
                        alt={country.name || user?.countryCode || 'Country'}
                    />
                </Tooltip>
            ) : (
                <Typography variant="caption" color="text.secondary">—</Typography>
            )}
            <Typography variant="caption" sx={{ textTransform: 'uppercase', lineHeight: 1 }}>
                {userLanguage(user)}
            </Typography>
        </Stack>
    );
}

function allowedGroupLabels(roundMatch: PrivateClubCurrentRoundMatchAdminDto) {
    return roundMatch.allowedBetTypeGroupIds.map(groupId => betGroupFallbackNames[groupId] ?? `Group ${groupId}`);
}

function privateClubTemplateBetsUrl(templateId: string, clubId: string, matchId?: number) {
    const params = new URLSearchParams({ clubIds: clubId });
    if (matchId != null) params.set('matchIds', String(matchId));
    return `/private-clubs/templates/${encodeURIComponent(templateId)}/placed-bets?${params.toString()}`;
}

function futureScheduledRounds(template?: PrivateClubTemplateDto | null) {
    const now = Date.now();
    return (template?.rounds ?? [])
        .filter(round => round.status === 'SCHEDULED' && new Date(round.startsAt).getTime() > now)
        .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
}

function Section(props: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
    return (
        <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
                <Typography variant="h6">{props.title}</Typography>
                {props.action}
            </Stack>
            {props.children}
        </Box>
    );
}

function MatchRow({
    item,
    club,
}: {
    item: PrivateClubCurrentRoundMatchAdminDto;
    club: PrivateClubDto;
}) {
    const match = item.match_info;
    const matchId = item.matchId;
    const labels = allowedGroupLabels(item);
    const placedPicksCount = item.placedBetsCount;
    const picksUrl = privateClubTemplateBetsUrl(club.templateId, club.id, matchId);

    return (
        <Stack gap={0.75} sx={{ borderTop: '1px solid #eee', py: 1 }}>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0 }}>
                <PrivateClubMatchSummary matchId={matchId} match={match ?? null} link newTab={false} />
                <Chip
                    size="small"
                    label={item.settlementStatus}
                    color={item.settlementStatus === 'SETTLED' ? 'success' : item.settlementStatus === 'PENDING' ? 'warning' : 'default'}
                />
            </Stack>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ pl: { xs: 0, sm: 4.5 } }}>
                <Typography variant="caption" color="text.secondary">
                    Allowed groups: {labels.length > 0 ? labels.join(', ') : '-'}
                </Typography>
                <MuiLink href={picksUrl} target="_blank" rel="noreferrer" underline="none">
                    <Chip size="small" variant="outlined" clickable label={`${placedPicksCount} picks`} />
                </MuiLink>
            </Stack>
        </Stack>
    );
}

export default function PrivateClubPage() {
    const navigate = useNavigate();
    const { userId: userIdParam = '', clubId = '' } = useParams<{ userId: string; clubId: string }>();
    const userId = Number(userIdParam);

    const [club, setClub] = React.useState<PrivateClubDto | null>(null);
    const [template, setTemplate] = React.useState<PrivateClubTemplateDto | null>(null);
    const [currentRound, setCurrentRound] = React.useState<PrivateClubCurrentRoundAdminDto | null>(null);
    const [currentRoundMatches, setCurrentRoundMatches] = React.useState<PrivateClubCurrentRoundMatchAdminDto[]>([]);
    const [leaderboard, setLeaderboard] = React.useState<PrivateClubLeaderboardEntryDto[]>([]);
    const [joinRequests, setJoinRequests] = React.useState<PrivateClubJoinRequestDto[]>([]);
    const [testingUserId, setTestingUserId] = React.useState<number | null>(null);
    const [addMemberDialogOpen, setAddMemberDialogOpen] = React.useState(false);
    const [rulesDialogOpen, setRulesDialogOpen] = React.useState(false);
    const [rulesText, setRulesText] = React.useState('');
    const [rulesSaving, setRulesSaving] = React.useState(false);
    const [joinRequestActionId, setJoinRequestActionId] = React.useState<string | null>(null);
    const [dismissing, setDismissing] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const load = React.useCallback(async () => {
        if (!userId || !clubId) {
            setError('Invalid private club route.');
            return;
        }
        setLoading(true);
        setError(null);
        const [clubResponse, templatesResponse, roundResponse, leaderboardResponse, joinRequestsResponse] = await Promise.all([
            getUserPrivateClub(userId, clubId),
            getPrivateClubTemplates(),
            getPrivateClubCurrentRoundMatches(clubId),
            getUserPrivateClubLeaderboard(userId, clubId),
            getPrivateClubJoinRequests(clubId, 0, 50),
        ]);

        setClub(clubResponse);
        setTemplate(templatesResponse?.find(item => item.id === clubResponse?.templateId) ?? null);
        setCurrentRound(roundResponse?.round ?? null);
        setCurrentRoundMatches(roundResponse?.matches ?? []);
        setLeaderboard(leaderboardResponse?.items ?? []);
        setJoinRequests(joinRequestsResponse?.items ?? []);
        setLoading(false);

        if (!clubResponse) setError('Failed to load private club.');
        else if (!templatesResponse) setError('Failed to load private club template schedule.');
        else if (!roundResponse) setError('Failed to load current round.');
        else if (!leaderboardResponse) setError('Failed to load leaderboard.');
        else if (!joinRequestsResponse) setError('Failed to load pending join requests.');
    }, [clubId, userId]);

    React.useEffect(() => {
        load();
    }, [load]);

    const futureRounds = React.useMemo(() => futureScheduledRounds(template), [template]);

    const openRulesEditor = () => {
        setRulesText(club?.rulesText ?? '');
        setRulesDialogOpen(true);
    };

    const saveRules = async () => {
        if (!club) return;
        setRulesSaving(true);
        setError(null);
        const result = await updateUserPrivateClub(club.createdBy, club.id, {
            rulesText: rulesText.trim() || '',
        });
        setRulesSaving(false);
        if (!result.ok) {
            setError(result.error ?? 'Private club rules were not updated.');
            return;
        }
        setRulesDialogOpen(false);
        await load();
    };

    const removeMember = async (memberUserId: number) => {
        if (!club || memberUserId === club.createdBy) return;
        if (!window.confirm(`Remove user ${memberUserId} from "${club.name}"? Their club-specific picks and score will be removed.`)) return;
        setError(null);
        const result = await removePrivateClubMember(club.id, memberUserId);
        if (!result.ok) {
            setError(result.error ?? 'Private club member was not removed.');
            return;
        }
        await load();
    };

    const approveJoinRequest = async (request: PrivateClubJoinRequestDto) => {
        if (!club || joinRequestActionId) return;
        setJoinRequestActionId(request.id);
        setError(null);
        const result = await approvePrivateClubJoinRequest(club.id, request.id);
        setJoinRequestActionId(null);
        if (!result.ok) {
            setError(result.error ?? 'Join request was not approved.');
            return;
        }
        await load();
    };

    const rejectJoinRequest = async (request: PrivateClubJoinRequestDto) => {
        if (!club || joinRequestActionId) return;
        setJoinRequestActionId(request.id);
        setError(null);
        const result = await rejectPrivateClubJoinRequest(club.id, request.id);
        setJoinRequestActionId(null);
        if (!result.ok) {
            setError(result.error ?? 'Join request was not rejected.');
            return;
        }
        await load();
    };

    const dismissClub = async () => {
        if (!club || club.status !== 'ACTIVE') return;
        if (!window.confirm(`Dismiss private club "${club.name}"? It cannot be reactivated.`)) return;
        setDismissing(true);
        setError(null);
        const result = await deleteUserPrivateClub(club.createdBy, club.id);
        setDismissing(false);
        if (!result.ok) {
            setError(result.error ?? 'Private club was not dismissed.');
            return;
        }
        navigate(`/users/${userId}?tab=privateClubs`);
    };

    if (!userId || !clubId) {
        return <Alert severity="error">Invalid private club route.</Alert>;
    }

    return (
        <Stack gap={2} sx={{ p: 2 }}>
            <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                <Stack gap={0.5}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/users/${userId}?tab=privateClubs`)} sx={{ alignSelf: 'flex-start' }}>
                        Back to user
                    </Button>
                    <Typography variant="h5">{club?.name ?? 'Private club'}</Typography>
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                        {club && <Chip size="small" label={club.status} color={statusColor(club.status)} />}
                        {template && <Chip size="small" variant="outlined" label={template.title} />}
                        <Typography variant="caption" color="text.secondary">{clubId}</Typography>
                    </Stack>
                </Stack>
                <Stack direction="row" gap={1}>
                    {club?.status === 'ACTIVE' && (
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={dismissClub}
                            disabled={loading || dismissing}
                        >
                            Dismiss
                        </Button>
                    )}
                    <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading || dismissing}>Refresh</Button>
                </Stack>
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}
            {loading && !club && (
                <Stack direction="row" gap={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Loading private club...</Typography>
                </Stack>
            )}

            {club && (
                <>
                    <Section title="Overview">
                        <Stack direction="row" gap={3} flexWrap="wrap">
                            <Box>
                                <Typography variant="caption" color="text.secondary">Created by</Typography>
                                <Typography variant="body2">
                                    <MuiLink
                                        component={RouterLink}
                                        to={`/users/${club.createdBy}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="hover"
                                    >
                                        {ownerName(club)}
                                    </MuiLink>
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Members</Typography>
                                <Typography variant="body2">{club.membersCount}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Invite token</Typography>
                                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{club.inviteToken ?? '-'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Join requests</Typography>
                                <Stack direction="row" gap={0.5} alignItems="center">
                                    <Chip
                                        size="small"
                                        label={club.acceptsJoinRequests ? 'Accepted' : 'Disabled'}
                                        color={club.acceptsJoinRequests ? 'success' : 'default'}
                                    />
                                </Stack>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Updated</Typography>
                                <Typography variant="body2">{formatDate(club.updatedAt)}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Reward mode</Typography>
                                <Stack direction="row" gap={0.5} alignItems="center">
                                    <Chip
                                        size="small"
                                        label={club.rewardMode}
                                        color={club.rewardMode === 'BETCOIN' ? 'primary' : 'default'}
                                    />
                                </Stack>
                            </Box>
                            {club.rewardMode === 'BETCOIN' && (
                                <>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Entry fee</Typography>
                                        <Typography variant="body2">{formatBetcoins(club.entryFeeBetcoins ?? 0)} betcoins</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Current bank</Typography>
                                        <Typography variant="body2">{formatBetcoins(club.bankBetcoins)} betcoins</Typography>
                                    </Box>
                                </>
                            )}
                        </Stack>
                    </Section>

                    <Section
                        title="Rules"
                        action={
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={openRulesEditor}
                            >
                                Edit
                            </Button>
                        }
                    >
                        {club.rulesText?.trim()
                            ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{club.rulesText}</Typography>
                            : <Typography variant="body2" color="text.secondary">No rules message provided.</Typography>}
                    </Section>

                    <Section
                        title="Pending Join Requests"
                        action={<Chip size="small" variant="outlined" label={`${joinRequests.length} active`} />}
                    >
                        {joinRequests.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No active join requests.</Typography>
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small" sx={{ minWidth: 720 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>User</TableCell>
                                            <TableCell>Locale</TableCell>
                                            <TableCell>Requested</TableCell>
                                            <TableCell>Expires</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {joinRequests.map(request => (
                                            <TableRow key={request.id}>
                                                <TableCell>
                                                    <UserIdentityCell userId={request.userId} user={request.user} />
                                                </TableCell>
                                                <TableCell>
                                                    <UserLocaleCell user={request.user} />
                                                </TableCell>
                                                <TableCell>{formatDate(request.createdAt)}</TableCell>
                                                <TableCell>{formatDate(request.expiresAt)}</TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" justifyContent="flex-end" gap={1}>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="success"
                                                            startIcon={joinRequestActionId === request.id ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />}
                                                            disabled={joinRequestActionId != null}
                                                            onClick={() => approveJoinRequest(request)}
                                                        >
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            startIcon={joinRequestActionId === request.id ? <CircularProgress size={14} color="inherit" /> : <CloseIcon />}
                                                            disabled={joinRequestActionId != null}
                                                            onClick={() => rejectJoinRequest(request)}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Section>

                    <Section
                        title="Leaderboard"
                        action={
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PersonAddIcon />}
                                onClick={() => setAddMemberDialogOpen(true)}
                            >
                                Add member
                            </Button>
                        }
                    >
                        {leaderboard.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No club members.</Typography>
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small" sx={{ minWidth: 1120 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Rank</TableCell>
                                            <TableCell>Member</TableCell>
                                            <TableCell>Locale</TableCell>
                                            <TableCell>Role</TableCell>
                                            <TableCell>Joined</TableCell>
                                            <TableCell align="right">Previous round</TableCell>
                                            <TableCell align="right">Current round</TableCell>
                                            <TableCell align="right">Total</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {leaderboard.map(entry => (
                                            <TableRow key={`${entry.rank}:${entry.user.id}`}>
                                                <TableCell>#{entry.rank}</TableCell>
                                                <TableCell>
                                                    <UserIdentityCell userId={entry.user.id} user={entry.user} />
                                                </TableCell>
                                                <TableCell>
                                                    <UserLocaleCell user={entry.user} />
                                                </TableCell>
                                                <TableCell>{entry.role}</TableCell>
                                                <TableCell>{formatDate(entry.joinedAt)}</TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2">{entry.prevRoundPoints} pts</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {entry.prevRoundCorrectPicks} / {entry.prevRoundTotalPicks} correct
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2">{entry.currentRoundPoints} pts</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {entry.currentRoundCorrectPicks} / {entry.currentRoundTotalPicks} correct
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2">{entry.totalPoints} pts</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {entry.totalCorrectPicks} / {entry.totalPicks} correct
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" justifyContent="flex-end">
                                                        <Tooltip title={`Test private club as ${userDisplayName(entry.user, entry.user.id)}`}>
                                                            <IconButton size="small" onClick={() => setTestingUserId(entry.user.id)}>
                                                                <VisibilityIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {entry.role !== 'ADMIN' && (
                                                            <Tooltip title={`Remove ${userDisplayName(entry.user, entry.user.id)} from club`}>
                                                                <IconButton size="small" color="error" onClick={() => removeMember(entry.user.id)}>
                                                                    <PersonRemoveIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Section>

                    <Section title="Current Round">
                        {!currentRound ? (
                            <Alert severity="info">There is no currently active round for this club.</Alert>
                        ) : (
                            <Stack gap={1}>
                                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                    <Typography variant="subtitle2">Round {currentRound.index}</Typography>
                                    <Chip size="small" label={currentRound.status} color={roundStatusColor(currentRound.status)} />
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDate(currentRound.startsAt)} - {formatDate(currentRound.endsAt)}
                                    </Typography>
                                    <Chip size="small" variant="outlined" label={`${currentRoundMatches.length} matches`} />
                                </Stack>
                                {currentRoundMatches.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">No matches in current round.</Typography>
                                ) : (
                                    <Stack>
                                        {currentRoundMatches.map(item => (
                                            <MatchRow
                                                key={`${currentRound.id}:${item.matchId}`}
                                                item={item}
                                                club={club}
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Stack>
                        )}
                    </Section>

                    <Section title="Future Rounds">
                        {!template ? (
                            <Alert severity="warning">Template schedule is not available.</Alert>
                        ) : futureRounds.length === 0 ? (
                            <Alert severity="info">No future rounds are scheduled for this club template.</Alert>
                        ) : (
                            <Stack gap={1} divider={<Divider />}>
                                <Typography variant="body2" color="text.secondary">
                                    {futureRounds.length} future scheduled round{futureRounds.length === 1 ? '' : 's'} found.
                                </Typography>
                                {futureRounds.slice(0, 10).map(round => (
                                    <Stack key={round.id} direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                        <Typography variant="body2">Round {round.index}</Typography>
                                        <Chip size="small" label={round.status} color={roundStatusColor(round.status)} />
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(round.startsAt)} - {formatDate(round.endsAt)}
                                        </Typography>
                                        <Chip size="small" variant="outlined" label={`${round.matches.filter(match => match.settlementStatus !== 'EXCLUDED').length} matches`} />
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Section>
                </>
            )}

            <PrivateClubTestAsUserDialog
                open={testingUserId != null && club != null}
                userId={testingUserId ?? userId}
                club={club}
                templates={template ? [template] : []}
                onClose={() => setTestingUserId(null)}
                onClubUpdated={updatedClub => {
                    setClub(updatedClub);
                    void load();
                }}
            />

            {club && (
                <AddPrivateClubMemberDialog
                    open={addMemberDialogOpen}
                    clubId={club.id}
                    clubName={club.name}
                    onClose={() => setAddMemberDialogOpen(false)}
                    onAdded={async () => {
                        await load();
                    }}
                />
            )}

            <Dialog open={rulesDialogOpen} onClose={() => setRulesDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Edit private club rules</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Rules"
                        value={rulesText}
                        onChange={event => setRulesText(event.target.value)}
                        fullWidth
                        multiline
                        minRows={4}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRulesDialogOpen(false)} disabled={rulesSaving}>Cancel</Button>
                    <Button variant="contained" onClick={saveRules} disabled={rulesSaving || !club}>
                        {rulesSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
